import * as THREE from 'three';
import { CARD_WIDTH, CARD_HEIGHT, CARD_THICKNESS } from '../cardMesh';

// ── Ward shield ──────────────────────────────────────────────────
//
// The stealth smoke shader, repainted in warm gold and switched
// from normal to additive blending. That's the whole concept.
// Stacked horizontal noise planes, same SDF envelope, same
// thresholded wisps, same lay-flat + yaw group transform. The only
// runtime differences:
//
//   • Colour uniforms are pale warm gold instead of near-pure black.
//   • Blending is AdditiveBlending instead of NormalBlending. So
//     the per-pixel density that previously darkened the background
//     now brightens it.
//   • Layer density is lowered (0.55 → 0.24) because additive on
//     bright colours saturates much faster than normal blending on
//     dark colours.
//
// Plus a sparse drift of soft warm motes (no rays, no stars. Pure
// radial halos) for "glittering" character on top of the aura.

const LAYER_COUNT = 8;
// Flat-to-the-card so stacked cards occlude the whole column via
// standard depth testing. See the matching comment in stealthSmoke.js.
const RISE_HEIGHT = 0.25;
const PLANE_W = CARD_WIDTH * 1.8;
const PLANE_H = CARD_HEIGHT * 1.5;

// Motes. Sparse, slow, subtle.
const MOTE_COUNT = 22;
const MOTE_LIFETIME = 4.0;
const MOTE_SIZE_MIN = 0.32;
const MOTE_SIZE_MAX = 0.95;
// Motes need to stay within the stacking thickness too, otherwise
// rising motes punch above stacked cards. Spawn range + this rise
// must keep them under ~0.32 world units above the card surface.
const MOTE_RISE = 0.08;
const MOTE_BORDER_BIAS = 0.7;
const MOTE_BORDER_JITTER = 1.5;

let _sharedMoteTexture = null;

/**
 * Pure radial-halo mote texture. Warm cream core fading into
 * transparent gold. No rays, no cross. Reads as a soft point of
 * light rather than a stylised magic sparkle.
 */
function getMoteTexture() {
  if (_sharedMoteTexture) return _sharedMoteTexture;
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  const cx = size / 2;
  const cy = size / 2;

  ctx.clearRect(0, 0, size, size);

  const halo = ctx.createRadialGradient(cx, cy, 0, cx, cy, size * 0.5);
  halo.addColorStop(0.00, 'rgba(255,247,220,0.55)');
  halo.addColorStop(0.18, 'rgba(255,235,180,0.35)');
  halo.addColorStop(0.45, 'rgba(255,215,140,0.12)');
  halo.addColorStop(1.00, 'rgba(255,210,140,0.00)');
  ctx.fillStyle = halo;
  ctx.fillRect(0, 0, size, size);

  const core = ctx.createRadialGradient(cx, cy, 0, cx, cy, size * 0.18);
  core.addColorStop(0.0, 'rgba(255,253,238,1.0)');
  core.addColorStop(0.4, 'rgba(255,250,225,0.6)');
  core.addColorStop(1.0, 'rgba(255,245,205,0.0)');
  ctx.fillStyle = core;
  ctx.beginPath();
  ctx.arc(cx, cy, size * 0.18, 0, Math.PI * 2);
  ctx.fill();

  const tex = new THREE.CanvasTexture(canvas);
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  _sharedMoteTexture = tex;
  return tex;
}

function pickBorderPoint(t, w, h) {
  const halfW = w / 2;
  const halfH = h / 2;
  const perimeter = 2 * (w + h);
  const d = t * perimeter;
  if (d < w)       return { x: -halfW + d,           y: halfH,                 ox: 0, oy: 1 };
  if (d < w + h)   return { x: halfW,                y: halfH - (d - w),       ox: 1, oy: 0 };
  if (d < 2*w + h) return { x: halfW - (d - w - h),  y: -halfH,                ox: 0, oy: -1 };
  return            { x: -halfW,               y: -halfH + (d - 2*w - h), ox: -1, oy: 0 };
}

// ── Aura shaders ─────────────────────────────────────────────────
//
// VERBATIM port of stealthSmoke's vertex + fragment shaders. The
// only line-level change is the comment block; uniforms and logic
// are identical. The blending behaviour is selected on the JS side
// via THREE.AdditiveBlending in the ShaderMaterial.

const AURA_VERT = /* glsl */ `
  uniform float uLayerT;

  varying vec2  vLocalXY;
  varying float vHeight;

  void main() {
    vLocalXY = position.xy;
    vHeight = uLayerT;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const AURA_FRAG = /* glsl */ `
  precision highp float;

  uniform float uTime;
  uniform vec2  uCardSize;
  uniform float uLayerDensity;
  uniform vec3  uColorCore;
  uniform vec3  uColorTint;

  varying vec2  vLocalXY;
  varying float vHeight;

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }
  float vnoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(hash(i),                hash(i + vec2(1.0, 0.0)), u.x),
      mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
      u.y
    );
  }
  float fbm(vec2 p) {
    float sum = 0.0;
    float amp = 0.5;
    for (int i = 0; i < 4; i++) {
      sum += amp * vnoise(p);
      p = p * 2.05 + vec2(1.7, 9.2);
      amp *= 0.55;
    }
    return sum;
  }
  // Domain-warped FBM. Same as stealth.
  float auraNoise(vec2 p) {
    vec2 warp = vec2(
      fbm(p + vec2(0.0, 0.0)),
      fbm(p + vec2(5.2, 1.3))
    );
    return fbm(p + warp * 1.9);
  }

  void main() {
    vec2 p = vLocalXY;

    vec2 q = abs(p) - uCardSize * 0.5;
    float sdf = length(max(q, vec2(0.0))) + min(max(q.x, q.y), 0.0);

    // Tighter than stealth's mix(2.4, 6.0) / mix(2.8, 6.5). The
    // ward sits close to the card silhouette instead of bleeding
    // far into the table.
    float outsideRange = mix(1.4, 3.5, vHeight);
    float insideRange  = mix(1.8, 4.0, vHeight);
    float insideEnv  = smoothstep(-insideRange, 0.5, sdf);
    float outsideEnv = 1.0 - smoothstep(0.5, outsideRange, sdf);
    float borderEnv = insideEnv * outsideEnv;
    if (borderEnv < 0.005) discard;

    float t = uTime * 0.32;
    vec2 noiseUv = p * 0.17
                 + vec2(sin(t * 0.45 + vHeight * 5.0) * 1.6, -t * 1.5)
                 + vec2(vHeight * 13.7, vHeight * 4.3);

    float n = auraNoise(noiseUv);

    float threshold = 0.28 + vHeight * 0.12;
    float wisps = smoothstep(threshold, 0.78, n);

    float density = borderEnv * wisps * uLayerDensity;
    if (density < 0.004) discard;

    vec3 col = mix(uColorCore, uColorTint, n);
    col *= mix(0.78, 1.0, density);

    // Same gl_FragColor shape as stealth: rgb = wisp colour, a = density.
    // Under AdditiveBlending (selected on the material), the SrcAlpha
    // src factor + One dst factor gives result.rgb = col*density + dst.
    // the wisps brighten the scene by the same density distribution that
    // stealth uses to darken it.
    gl_FragColor = vec4(col, density);
  }
`;

// ── Mote shaders ─────────────────────────────────────────────────

const MOTE_VERT = /* glsl */ `
  uniform float uTime;
  uniform float uLifetime;
  uniform float uSizeMin;
  uniform float uSizeMax;
  uniform float uRise;

  attribute vec3  aSpawnLocal;
  attribute float aSeed;
  attribute float aPhase;
  attribute float aLifeMult;

  varying vec2  vUv;
  varying float vAlpha;
  varying float vSeed;

  void main() {
    float lifetime = uLifetime * aLifeMult;
    float life = mod(uTime / lifetime + aPhase, 1.0);

    vec3 spawn = aSpawnLocal;
    float ang = aSeed * 6.2831853 + life * 1.1;
    spawn.x += cos(ang) * 0.32;
    spawn.y += sin(ang) * 0.32;
    spawn.z += life * uRise;

    vec4 mvCenter = modelViewMatrix * vec4(spawn, 1.0);

    float twinkleFreq = 0.55 + aSeed * 1.3;
    float twinkleRaw  = 0.5 + 0.5 * sin(uTime * twinkleFreq + aSeed * 31.0);
    float twinkle = pow(twinkleRaw, 2.2);

    vec2 corner = position.xy;
    float baseSize = mix(uSizeMin, uSizeMax, fract(aSeed * 7.13));
    float size = baseSize * (0.7 + 0.3 * twinkle);
    mvCenter.xy += corner * size;

    gl_Position = projectionMatrix * mvCenter;

    float fadeIn  = smoothstep(0.0, 0.25, life);
    float fadeOut = 1.0 - smoothstep(0.6, 1.0, life);
    vAlpha = fadeIn * fadeOut * (0.5 + 0.5 * twinkle);

    vUv = uv;
    vSeed = aSeed;
  }
`;

const MOTE_FRAG = /* glsl */ `
  precision highp float;

  uniform sampler2D uTexture;
  uniform vec3 uColorCore;
  uniform vec3 uColorTint;

  varying vec2  vUv;
  varying float vAlpha;
  varying float vSeed;

  void main() {
    vec4 tex = texture2D(uTexture, vUv);
    float a = tex.a * vAlpha;
    if (a < 0.003) discard;
    vec3 col = mix(uColorCore, uColorTint, fract(vSeed * 31.713));
    gl_FragColor = vec4(col * a, 1.0);
  }
`;

export function createWardShieldEffect() {
  const group = new THREE.Group();
  group.frustumCulled = false;

  const tSeed = Math.random() * 100;

  // ── AURA: 1:1 port of stealth's stacked planes, gold + additive ──
  const auraGeometry = new THREE.PlaneGeometry(PLANE_W, PLANE_H);
  const auraMaterials = [];

  for (let i = 0; i < LAYER_COUNT; i++) {
    const layerT = i / Math.max(LAYER_COUNT - 1, 1);
    const layerHeight = layerT * RISE_HEIGHT;
    // Stealth uses 0.55 base; ward uses 0.17 because additive on
    // bright gold accumulates much faster than normal blending on
    // near-black, and we want the effect subtle. Exponent (2.0)
    // unchanged from stealth.
    const layerDensity = Math.exp(-2.0 * layerT) * 0.17;

    const material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: tSeed },
        uCardSize: { value: new THREE.Vector2(CARD_WIDTH, CARD_HEIGHT) },
        uLayerT: { value: layerT },
        uLayerDensity: { value: layerDensity },
        // Warm pale gold core, cream tint. The only essential
        // difference from stealth's `0x040408 / 0x1a1326` palette.
        uColorCore: { value: new THREE.Color(0xffd680) },
        uColorTint: { value: new THREE.Color(0xfff0c8) },
      },
      vertexShader: AURA_VERT,
      fragmentShader: AURA_FRAG,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });

    const mesh = new THREE.Mesh(auraGeometry, material);
    mesh.frustumCulled = false;
    // Flat-to-card positioning. See stealthSmoke's matching comment.
    mesh.position.z = layerHeight + CARD_THICKNESS / 2 + 0.05;
    // Sits under stealth smoke (12+) when both are active so the
    // smoke composites on top of the warm tint cleanly.
    mesh.renderOrder = 9 + i;
    group.add(mesh);
    auraMaterials.push(material);
  }

  // ── MOTES: sparse soft halos drifting around the silhouette ────
  const moteQuad = new THREE.PlaneGeometry(1, 1);
  const moteGeometry = new THREE.InstancedBufferGeometry();
  moteGeometry.index = moteQuad.index;
  moteGeometry.setAttribute('position', moteQuad.getAttribute('position'));
  moteGeometry.setAttribute('uv', moteQuad.getAttribute('uv'));
  moteQuad.dispose();

  const spawnLocal = new Float32Array(MOTE_COUNT * 3);
  const seeds = new Float32Array(MOTE_COUNT);
  const phases = new Float32Array(MOTE_COUNT);
  const lifeMults = new Float32Array(MOTE_COUNT);

  // Just barely above the card surface. Motes' spawn + MOTE_RISE
  // must stay within ~CARD_THICKNESS of the card top so they're
  // occluded by any stacked card above.
  const baseZ = CARD_THICKNESS / 2 + 0.01;
  for (let i = 0; i < MOTE_COUNT; i++) {
    let x;
    let y;
    if (Math.random() < MOTE_BORDER_BIAS) {
      const t = Math.random();
      const pt = pickBorderPoint(t, CARD_WIDTH, CARD_HEIGHT);
      const j = (Math.random() * 2 - 1) * MOTE_BORDER_JITTER;
      x = pt.x + pt.ox * j;
      y = pt.y + pt.oy * j;
    } else {
      x = (Math.random() * 2 - 1) * (CARD_WIDTH  / 2 + 1.6);
      y = (Math.random() * 2 - 1) * (CARD_HEIGHT / 2 + 1.6);
    }
    spawnLocal[i * 3]     = x;
    spawnLocal[i * 3 + 1] = y;
    // Spawn within a thin band above the card surface. Combined
    // with MOTE_RISE, max world-Y above card is ~baseZ + 0.13 +
    // 0.08 ≈ 0.22, well under CARD_THICKNESS so stacked cards
    // cover the entire mote field.
    spawnLocal[i * 3 + 2] = baseZ + Math.random() * 0.13;

    seeds[i] = Math.random();
    phases[i] = Math.random();
    lifeMults[i] = 0.6 + Math.random() * 0.8;
  }

  moteGeometry.setAttribute('aSpawnLocal', new THREE.InstancedBufferAttribute(spawnLocal, 3));
  moteGeometry.setAttribute('aSeed',       new THREE.InstancedBufferAttribute(seeds, 1));
  moteGeometry.setAttribute('aPhase',      new THREE.InstancedBufferAttribute(phases, 1));
  moteGeometry.setAttribute('aLifeMult',   new THREE.InstancedBufferAttribute(lifeMults, 1));
  moteGeometry.instanceCount = MOTE_COUNT;

  const moteMaterial = new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: tSeed },
      uLifetime: { value: MOTE_LIFETIME },
      uSizeMin: { value: MOTE_SIZE_MIN },
      uSizeMax: { value: MOTE_SIZE_MAX },
      uRise: { value: MOTE_RISE },
      uTexture: { value: getMoteTexture() },
      uColorCore: { value: new THREE.Color(0xfff6d8) },
      uColorTint: { value: new THREE.Color(0xffe1a4) },
    },
    vertexShader: MOTE_VERT,
    fragmentShader: MOTE_FRAG,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: THREE.AdditiveBlending,
  });

  const motes = new THREE.Mesh(moteGeometry, moteMaterial);
  motes.frustumCulled = false;
  motes.renderOrder = 30;
  group.add(motes);

  return {
    points: group,
    auraMaterials,
    auraGeometry,
    moteMaterial,
    moteGeometry,
  };
}

const _tmpVec3 = new THREE.Vector3();
const _tmpEuler = new THREE.Euler();
export function updateWardShieldEffect(effect, dt, cardMesh) {
  if (!effect || !cardMesh) return;

  for (const material of effect.auraMaterials) {
    material.uniforms.uTime.value += dt;
  }
  effect.moteMaterial.uniforms.uTime.value += dt;

  cardMesh.getWorldPosition(_tmpVec3);
  effect.points.position.copy(_tmpVec3);

  const intent = cardMesh.userData?.intentEuler;
  const yaw = intent ? intent.z : 0;
  _tmpEuler.set(-Math.PI / 2, 0, yaw, 'XYZ');
  effect.points.rotation.copy(_tmpEuler);
}

export function disposeWardShieldEffect(effect) {
  if (!effect) return;
  effect.points.parent?.remove(effect.points);
  effect.auraGeometry?.dispose();
  for (const material of effect.auraMaterials || []) {
    material.dispose();
  }
  effect.moteGeometry?.dispose();
  effect.moteMaterial?.dispose();
}
